/**
 * useGlobalFilters.js - Hook para gerenciamento de filtros globais
 * CG Business ERP - P2
 */

import { useState, useCallback, useMemo } from 'react';

const FILTER_KEYS_WITH_ALL_SENTINEL = ['location_id', 'employee_id', 'status', 'category', 'payment_account_id'];

export function useGlobalFilters(initialFilters = {}) {
  const [filters, setFilters] = useState({
    period: 'all',
    location_id: null,
    employee_id: null,
    status: null,
    category: null,
    payment_account_id: null,
    deleted: 'active',
    custom_start: null,
    custom_end: null,
    ...initialFilters,
  });
  
  const handleFilterChange = useCallback((newFilters) => {
    const normalizedFilters = { ...newFilters };

    FILTER_KEYS_WITH_ALL_SENTINEL.forEach((key) => {
      if (normalizedFilters[key] === 'all') {
        normalizedFilters[key] = null;
      }
    });

    setFilters(prev => {
      const next = {
        ...prev,
        ...normalizedFilters,
      };

      if (Object.prototype.hasOwnProperty.call(normalizedFilters, 'period') && normalizedFilters.period !== 'custom') {
        next.custom_start = normalizedFilters.custom_start ?? null;
        next.custom_end = normalizedFilters.custom_end ?? null;
      }

      return next;
    });
  }, []);
  
  const clearFilters = useCallback(() => {
    setFilters({
      period: 'all',
      location_id: null,
      employee_id: null,
      status: null,
      category: null,
      payment_account_id: null,
      deleted: 'active',
      custom_start: null,
      custom_end: null,
    });
  }, []);
  
  // Build query params for API calls
  const queryParams = useMemo(() => {
    const params = {};
    if (filters.period && filters.period !== 'all') params.period = filters.period;
    if (filters.location_id) params.location_id = filters.location_id;
    if (filters.employee_id) params.employee_id = filters.employee_id;
    if (filters.status) params.status = filters.status;
    if (filters.category) params.category = filters.category;
    if (filters.payment_account_id) params.payment_account_id = filters.payment_account_id;
    if (filters.deleted !== 'active') {
      params.include_deleted = filters.deleted === 'all';
      params.deleted_only = filters.deleted === 'deleted';
    }
    if (filters.custom_start) params.start_date = filters.custom_start;
    if (filters.custom_end) params.end_date = filters.custom_end;
    return params;
  }, [filters]);
  
  return {
    filters,
    setFilters: handleFilterChange,
    clearFilters,
    queryParams,
  };
}

export default useGlobalFilters;
